"use client"

import { useTranslations } from "next-intl"

/**
 * Guardar la tarjeta, con una vista previa de lo que el cliente va a recibir.
 *
 * No decide NADA de plataforma: el servidor que creó la orden ya resolvió eso y
 * aquí solo llega el link o `null`. Sin `navigator` y sin `useEffect` a propósito
 * —leer la plataforma en el cliente daría mismatch de hidratación— y por eso se
 * puede probar con renderToStaticMarkup, que es la única infra de componentes
 * que tiene este repo.
 *
 * LA VISTA PREVIA ES EL ARGUMENTO. Un botón que dice "guarda tu tarjeta" pide un
 * favor; una miniatura del pase con el riel de progreso enseña qué se gana. Va en
 * carbón con el mismo riel que la franja real, para que al tocarlo el cliente
 * reconozca lo que aparece en su Wallet.
 *
 * Va en carbón y no en el acento del restaurante porque el pase es de Redy, y eso
 * lo separa del botón "Listo", que sí lleva el color del local: dos acciones
 * distintas no pueden verse iguales.
 */
export function WalletSaveButton({ url }: { url: string | null }) {
  const t = useTranslations("public.onlineOrder.checkout")
  if (!url) return null

  return (
    <a
      href={url}
      className="block w-full overflow-hidden rounded-2xl transition hover:opacity-95 active:scale-[0.99]"
      style={{ backgroundColor: "#121214" }}
    >
      {/* La miniatura: el mismo riel de tres hitos que lleva la franja del pase. */}
      <div className="px-5 pt-5 pb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
          {t("walletPreviewLabel")}
        </p>
        <p className="mt-1.5 text-[19px] font-extrabold leading-tight text-white">
          {t("walletPreviewStatus")}
        </p>
        <div className="mt-3.5 flex items-center gap-2" aria-hidden="true">
          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-white" />
          <span className="h-[3px] flex-1 rounded-full bg-white" />
          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-white" />
          <span className="h-[3px] flex-1 rounded-full bg-white/25" />
          <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full border-[2px] border-white/40" />
        </div>
        <div className="mt-2 flex justify-between text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/45">
          <span>{t("walletStepReceived")}</span>
          <span>{t("walletStepKitchen")}</span>
          <span>{t("walletStepReady")}</span>
        </div>
      </div>

      {/* La zona de toque tiene que PARECER un botón. La tarjeta entera es el
          enlace, pero sin una superficie sólida el cliente no sabe dónde pulsar:
          un bloque oscuro con texto se lee como un cartel, no como una acción.
          Blanco sobre carbón es el contraste más alto disponible aquí y lo separa
          del resto del bloque, que es contexto. */}
      <div className="px-4 pb-4">
        <span className="block rounded-xl bg-white px-4 py-3.5 text-center text-[15px] font-bold leading-tight text-[#121214]">
          {t("saveToWallet")}
        </span>
        <span className="mt-2.5 block px-1 text-center text-[12px] leading-snug text-white/55">
          {t("saveToWalletHint")}
        </span>
      </div>
    </a>
  )
}
